/* Режимы экрана в одном кадре движка (engine-kit, P1): «Фото» и «Видео» шаблона меняют режим на месте («Из фото · По описанию»,
   «Без стиля» ↔ стиль, фото ↔ ролик — renderBody шаблона), а сервер публикует по приложению на режим. Кадр не меняется: ключ,
   разметка движка и его S остаются, меняется сервис заказа, на который смотрит сервер-объект экрана.
   ModeServices — сервисы режимов рядом с сервисом кадра (его держит EngineFrame): текущий режим и прежний, пока у текущего не
   пришла форма (как удержание тела useHeldMode у родных форм). У каждого свой useOrderService и свой знак заказа — «показан»
   у текущего режима активного кадра. Ушедший сервис снимается, как родное удержанное тело: его заявка остаётся в sessionStorage.
   Смена — ядро mode-switch.ts (владелец до и после GET, тот же экран, черновики, 30 с). Объект режимов (EngineModes) один на кадр:
   он уходит в каждую привязку движка (EngineBinding.modes). Любой вид любого режима — bound.changed() кадра. React разметки не даёт. */
import { memo, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { UserAvatar } from '../../../api/people';
import type { StudioApp } from '../studio-api';
import { useHeldMode } from '../use-held-mode';
import { engineKind, type EngineModes, type EngineScreen } from './engine-host';
import { createModeSwitch } from './mode-switch';
import type { OrderService, StudioFormView } from './order-service';
import { useOrderBadge } from './use-order-badge';
import { useOrderService } from './use-order-service';

export interface ModeServicesProps {
  /** Приложение кадра: его сервис держит EngineFrame (EngineBinding.svc) */
  app: StudioApp;
  svc: OrderService;
  /** Вид сервиса кадра: ModeServices рендерится на каждый — знак кадра и готовность его режима читаются с него */
  view: StudioFormView;
  avatar: UserAvatar | null;
  initialInputs?: Record<string, unknown>;
  /** Кадр активен */
  active: boolean;
  engine: EngineScreen;
  /** Объект режимов кадра (до привязки: эффекты компоновки детей идут раньше) и null при снятии */
  onModes(modes: EngineModes | null): void;
  /** Вид сервиса режима изменился → bound.changed() кадра */
  onView(): void;
}

/** Реестр сервисов режимов одного кадра */
interface Group {
  modes: EngineModes;
  service(id: number, svc: OrderService | null, was?: OrderService): void;
  ready(id: number): void;
  dispose(): void;
}

export function ModeServices({ app, svc, view, avatar, initialInputs, active, engine, onModes, onView }: ModeServicesProps) {
  const [core] = useState(() => createModeSwitch(app, initialInputs, String(app.entry?.screen ?? '')));
  const mode = useSyncExternalStore(core.subscribe, core.get);
  useEffect(() => core.attach(), [core]);
  const { bodies, ready } = useHeldMode(mode.active, mode.seed);
  const heldReady = useRef(ready);
  useLayoutEffect(() => {
    heldReady.current = ready;
  });
  const [group] = useState<Group>(() => {
    const services = new Map<number, OrderService>();
    const waiters = new Map<number, Array<(found: OrderService | null) => void>>();
    let current = { app, svc };
    const byApp = (id: number) => (id === app.id ? svc : (services.get(id) ?? null));
    /** Сервис режима, когда он смонтирован (ModeService ставит его в эффекте компоновки) */
    const mounted = (id: number) =>
      new Promise<OrderService | null>(resolve => {
        const found = byApp(id);
        if (found) resolve(found);
        else waiters.set(id, [...(waiters.get(id) ?? []), resolve]);
      });
    return {
      modes: {
        current: () => current,
        byApp,
        async switchTo(id, seed, o = {}) {
          const ok = await core.switchMode(id, seed, current.svc.get().identity?.owner, !!o.transfer);
          if (!ok) return false;
          const next = await mounted(id);
          if (!next || core.get().active.id !== id) return false;
          current = { app: core.get().active, svc: next };
          // то же согласие на те же файлы (Q-PV1): экран переносит его только вместе с файлами
          if (o.rights) next.setters.rights(true);
          return true;
        },
        busy: () => core.get().switching,
      },
      service(id, found, was) {
        if (found) {
          services.set(id, found);
          for (const resolve of waiters.get(id) ?? []) resolve(found);
          waiters.delete(id);
        } else if (services.get(id) === was) services.delete(id);
      },
      ready: id => heldReady.current(id),
      dispose() {
        for (const list of waiters.values()) for (const resolve of list) resolve(null);
        waiters.clear();
      },
    };
  });
  useLayoutEffect(() => {
    onModes(group.modes);
    return () => {
      onModes(null);
      group.dispose();
    };
  }, [group, onModes]);
  /* знак сервиса кадра — здесь: «показан», пока режим кадра текущий (EngineFrame знак не публикует) */
  useOrderBadge(svc, s => engineKind(engine, s, app), active && mode.active.id === app.id);
  /* вернулись в режим кадра: его форма уже есть — прежний режим больше не держится */
  const frameReady = !!view.form || !!view.error;
  useEffect(() => {
    if (frameReady) ready(app.id);
  }, [frameReady, ready, app.id]);
  return bodies
    .filter(body => body.app.id !== app.id)
    .map(body => (
      <ModeService
        key={body.app.id}
        app={body.app}
        seed={body.seed}
        avatar={avatar}
        engine={engine}
        shown={active && body.app.id === mode.active.id}
        group={group}
        onView={onView}
      />
    ));
}

interface ModeServiceProps {
  app: StudioApp;
  seed: Record<string, unknown> | undefined;
  avatar: UserAvatar | null;
  engine: EngineScreen;
  /** Знак заказа «показан»: текущий режим активного кадра */
  shown: boolean;
  group: Group;
  onView(): void;
}

/** Сервис одного режима (не приложения кадра): рендерится только на свои виды */
const ModeService = memo(function ModeService({ app, seed, avatar, engine, shown, group, onView }: ModeServiceProps) {
  const { svc, view } = useOrderService({ app, avatar, initialInputs: seed });
  useOrderBadge(svc, s => engineKind(engine, s, app), shown);
  useLayoutEffect(() => {
    group.service(app.id, svc);
    return () => group.service(app.id, null, svc);
  }, [group, app.id, svc]);
  useEffect(() => {
    onView();
  }, [onView, view]);
  /* форма режима пришла (или не пришла): прежний режим можно отпустить */
  const formReady = !!view.form || !!view.error;
  useEffect(() => {
    if (formReady) group.ready(app.id);
  }, [group, formReady, app.id]);
  return null;
});
